import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import type { Control } from "react-hook-form";
import { useWatch } from "react-hook-form";
import { useSlugValidation } from "../../hooks/use-slug-validation";
import type { McpServerCreateRequest } from "../../types";
import { SlugAvailabilityIndicator } from "./slug-availability-indicator";

type SlugFormFieldProps = {
  control: Control<McpServerCreateRequest>;
  originalSlug?: string;
};

export const SlugFormField = ({
  control,
  originalSlug,
}: SlugFormFieldProps) => {
  const slugValue = useWatch({ control, name: "slug" });
  const { isAvailable, shouldShowIndicator, error, hasError, isValidating } =
    useSlugValidation(slugValue || "", originalSlug);

  const getValidationMessage = () => {
    if (shouldShowIndicator && isValidating) {
      return "Checking availability...";
    }
    if (shouldShowIndicator && hasError && error) {
      return error;
    }
    if (shouldShowIndicator && isAvailable === false) {
      return "This slug is already taken. Please choose a different one.";
    }
    if (shouldShowIndicator && isAvailable === true) {
      return "This slug is available.";
    }
    return null;
  };

  const getValidationDescription = () => {
    const validationMessage = getValidationMessage();

    if (validationMessage) {
      return validationMessage;
    }

    return "Optional. A URL‑friendly identifier for this gateway (letters, numbers, _ and -).";
  };

  const getValidationDescriptionClassName = () => {
    if (shouldShowIndicator && isValidating) {
      return "text-muted-foreground";
    }
    if (shouldShowIndicator && hasError) {
      return "text-destructive";
    }
    if (shouldShowIndicator && isAvailable === false) {
      return "text-destructive";
    }
    if (shouldShowIndicator && isAvailable === true) {
      return "text-success";
    }
    return "text-muted-foreground";
  };

  return (
    <FormField
      control={control}
      name="slug"
      render={({ field }) => (
        <FormItem>
          <FormLabel>URL slug (optional)</FormLabel>
          <FormControl>
            <div className="relative">
              <Input
                {...field}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^A-Za-z0-9_-]/g, "");
                  field.onChange(value || null);
                }}
                value={field.value ?? ""}
              />
              <SlugAvailabilityIndicator
                originalSlug={originalSlug}
                slug={field.value || ""}
              />
            </div>
          </FormControl>

          {/* Validation status below input */}
          <FormDescription className={getValidationDescriptionClassName()}>
            {getValidationDescription()}
          </FormDescription>

          <FormMessage />
        </FormItem>
      )}
    />
  );
};
